import * as google_protobuf_empty_pb from 'google-protobuf/google/protobuf/empty_pb';
import * as google_protobuf_struct_pb from 'google-protobuf/google/protobuf/struct_pb';

import publisherBillingService from '@api/services/billingPublisherService';
import request from '@api/base/request';

import {
  BillingPublisherAddressType,
  BillingPublisherInvoiceType,
  BillingPublisherPayoutFeePreviewResponseType,
  BillingPublisherPayoutRequirementsResponseType,
  BillingPublisherPayoutSettingsResponseType,
  BillingPublisherType,
  BillingPublisherWithdrawalResponseType,
  BillingPublisherWithdrawalType,
} from '@ts/billing-publisher.types';
import BillingPublisherModels, {
  BillingPublisherPaymentBankAccountSettings,
  BillingPublisherPaymentPayPalSettings,
  BillingPublisherPaymentWiseAccountSettings,
  BillingPublisherPayoutFeePreviewRequest,
  BillingPublisherPayoutRemovingRequest,
  BillingPublisherPayoutRequirementsRequest,
  BillingPublisherPayoutSettings,
  BillingPublisherPayoutVerificationRequest,
  BillingPublisherWithdrawalRequest,
  PublisherInvoiceRequest,
  PublisherWithdrawalRequest,
} from 'respona_api/generated/billing-publisher_pb';
import { DateRangeNumberType, FileResponseType } from '@ts/common.types';
import * as common_pb from 'respona_api/generated/common_pb';
import CommonModels, {
  BillingPublisherPaymentMethod,
  BillingPublisherPaymentMethodMap,
} from 'respona_api/generated/common_pb';
import streamRequest from '@api/base/streamRequest';

type PaymentMethodValue = BillingPublisherPaymentMethodMap[keyof BillingPublisherPaymentMethodMap];

// ================================================================================ BILLING

export const getPublisherBillingDetailsApi = (): Promise<BillingPublisherType> => {
  const client = publisherBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<BillingPublisherType>(client, client.getBillingDetails, emptyRequest);
};

export type BankAccountPayoutInput = {
  method: BillingPublisherPaymentMethodMap['BANK_ACCOUNT'];
  // Destination currency (ISO 4217). e.g. "EUR".
  currency: string;
  // Account holder name (single string as registered with the bank).
  // Top-level Wise field — not inside `details`.
  accountHolderName: string;
  // Wise recipient type, taken verbatim from PayoutBankAccountType.type
  // (e.g. "aba", "iban", "sort_code", "swift_code", "indian").
  type: string;
  // Flat map of Wise `details` field keys (dotted for nested paths,
  // e.g. "address.country") → user-entered value. Includes `legalType` and
  // every corridor-specific field. Forwarded straight through to Wise.
  details: Record<string, string>;
};

// Wise-native "Wise account" payout — a Wise recipient of type "email",
// identified only by the recipient's Wise account email. The backend creates
// the recipient + transfer + BALANCE funding; for the default USD->USD
// corridor the transfer is Wise-to-Wise (instant, zero fee).
export type WiseAccountPayoutInput = {
  method: BillingPublisherPaymentMethodMap['WISE_ACCOUNT'];
  email: string;
  accountHolderName: string;
};

export type PayoutSettingsInput =
  | {
      method: BillingPublisherPaymentMethodMap['PAYPAL'];
      email: string;
      transferMethodCountry: string;
      transferMethodCurrency: string;
    }
  | BankAccountPayoutInput
  | WiseAccountPayoutInput;

// Converts a flat map of dotted-key Wise field values (e.g. "address.country" -> "DE")
// into a nested object shape, then into a google.protobuf.Struct. Mirrors the Wise
// `details` object shape consumed by createOrUpdatePayoutSettings and
// getPayoutBankAccountRequirements.
const buildDetailsStruct = (
  details: Record<string, string>
): google_protobuf_struct_pb.Struct => {
  const root: Record<string, unknown> = {};
  Object.entries(details).forEach(([key, value]) => {
    if (value == null || value === '') return;
    const path = key.split('.');
    let cursor = root;
    for (let i = 0; i < path.length - 1; i += 1) {
      const segment = path[i];
      if (!cursor[segment] || typeof cursor[segment] !== 'object') {
        cursor[segment] = {};
      }
      cursor = cursor[segment] as Record<string, unknown>;
    }
    cursor[path[path.length - 1]] = value;
  });
  return google_protobuf_struct_pb.Struct.fromJavaScript(root);
};

export const createOrUpdatePayoutSettingsApi = (
  input: PayoutSettingsInput
): Promise<BillingPublisherPayoutSettingsResponseType> => {
  const client = publisherBillingService.getInstance();
  const payoutRequest = new BillingPublisherPayoutSettings();
  payoutRequest.setPaymentMethod(input.method);

  if (input.method === BillingPublisherPaymentMethod.PAYPAL) {
    const paypalSettings = new BillingPublisherPaymentPayPalSettings();
    paypalSettings.setEmail(input.email);
    paypalSettings.setActive(true);
    if (input.transferMethodCountry) {
      paypalSettings.setTransferMethodCountry(input.transferMethodCountry);
    }
    if (input.transferMethodCurrency) {
      paypalSettings.setTransferMethodCurrency(input.transferMethodCurrency);
    }
    payoutRequest.setPayPal(paypalSettings);
  } else if (input.method === BillingPublisherPaymentMethod.BANK_ACCOUNT) {
    const bankSettings = new BillingPublisherPaymentBankAccountSettings();
    bankSettings.setCurrency(input.currency);
    bankSettings.setAccountHolderName(input.accountHolderName);
    bankSettings.setType(input.type);
    bankSettings.setDetails(buildDetailsStruct(input.details));
    payoutRequest.setBankAccount(bankSettings);
  } else if (input.method === BillingPublisherPaymentMethod.WISE_ACCOUNT) {
    const wiseSettings = new BillingPublisherPaymentWiseAccountSettings();
    wiseSettings.setEmail(input.email);
    wiseSettings.setAccountHolderName(input.accountHolderName);
    payoutRequest.setWiseAccount(wiseSettings);
  }

  return request<BillingPublisherPayoutSettingsResponseType>(
    client,
    client.createOrUpdatePayoutSettings,
    payoutRequest
  );
};

export const getPayoutBankAccountRequirementsApi = (
  currency: string,
  country: string,
  partialDetails?: Record<string, string>
): Promise<BillingPublisherPayoutRequirementsResponseType> => {
  const client = publisherBillingService.getInstance();
  const req = new BillingPublisherPayoutRequirementsRequest();
  req.setCurrency(currency);
  req.setCountry(country);
  if (partialDetails && Object.keys(partialDetails).length > 0) {
    req.setPartialDetails(buildDetailsStruct(partialDetails));
  }

  return request<BillingPublisherPayoutRequirementsResponseType>(
    client,
    client.getPayoutBankAccountRequirements,
    req
  );
};

export const getPayoutBankAccountFeePreviewApi = (
  targetCurrency: string,
  sourceAmount: number,
  recipientId?: string
): Promise<BillingPublisherPayoutFeePreviewResponseType> => {
  const client = publisherBillingService.getInstance();
  const req = new BillingPublisherPayoutFeePreviewRequest();
  req.setTargetCurrency(targetCurrency);
  req.setSourceAmount(sourceAmount);
  if (recipientId) {
    req.setRecipientId(recipientId);
  }

  return request<BillingPublisherPayoutFeePreviewResponseType>(
    client,
    client.getPayoutBankAccountFeePreview,
    req
  );
};

export const removePayoutSettingsApi = (method: PaymentMethodValue): Promise<void> => {
  const client = publisherBillingService.getInstance();
  const removeRequest = new BillingPublisherPayoutRemovingRequest();
  removeRequest.setPaymentMethod(method);

  return request<void>(client, client.removePayoutSettings, removeRequest);
};

export const verifyPayoutValidationCodeApi = (
  code: string,
  method: PaymentMethodValue = BillingPublisherPaymentMethod.PAYPAL
): Promise<BillingPublisherPayoutSettingsResponseType> => {
  const client = publisherBillingService.getInstance();
  const verifyRequest = new BillingPublisherPayoutVerificationRequest();
  verifyRequest.setPaymentMethod(method);
  verifyRequest.setVerificationCode(code);

  return request<BillingPublisherPayoutSettingsResponseType>(
    client,
    client.verifyPayoutValidationCode,
    verifyRequest
  );
};

export const resendPayoutValidationCodeApi =
  (): Promise<BillingPublisherPayoutSettingsResponseType> => {
    const client = publisherBillingService.getInstance();
    const emptyRequest = new google_protobuf_empty_pb.Empty();

    return request<BillingPublisherPayoutSettingsResponseType>(
      client,
      client.resendPayoutValidationCode,
      emptyRequest
    );
  };

export const makeWithdrawalApi = (
  amount: number,
  method: PaymentMethodValue = BillingPublisherPaymentMethod.PAYPAL
): Promise<BillingPublisherWithdrawalResponseType> => {
  const client = publisherBillingService.getInstance();

  const withdrawalRequest = new BillingPublisherWithdrawalRequest();
  withdrawalRequest.setAmount(amount);
  withdrawalRequest.setPaymentMethod(method);

  return request<BillingPublisherWithdrawalResponseType>(
    client,
    client.makeWithdrawal,
    withdrawalRequest,
    true,
    false
  );
};

// ================================================================================ BILLING_ADDRESS
export const getPublisherBillingAddressApi = (): Promise<BillingPublisherAddressType> => {
  const client = publisherBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<BillingPublisherAddressType>(client, client.getBillingAddress, emptyRequest);
};

export const updatePublisherBillingAddressApi = (
  payload: Partial<BillingPublisherAddressType>
): Promise<BillingPublisherAddressType> => {
  const client = publisherBillingService.getInstance();
  const req = new BillingPublisherModels.BillingPublisherAddress();

  if (payload.addressId != null) req.setAddressId(payload.addressId);
  if (payload.companyName != null) req.setCompanyName(payload.companyName);
  if (payload.firstLine != null) req.setFirstLine(payload.firstLine);
  if (payload.secondLine != null) req.setSecondLine(payload.secondLine);
  if (payload.city != null) req.setCity(payload.city);
  if (payload.zipCode != null) req.setZipCode(payload.zipCode);
  if (payload.state != null) req.setState(payload.state);
  if (payload.country != null) req.setCountry(payload.country);
  if (payload.contactEmail != null) req.setContactEmail(payload.contactEmail);
  if (payload.contactName != null) req.setContactName(payload.contactName);
  if (payload.tax != null) req.setTax(payload.tax);
  if (payload.dateOfBirth != null) req.setDateOfBirth(payload.dateOfBirth);
  if (payload.payoutProfileType != null) req.setPayoutProfileType(payload.payoutProfileType);

  // W-8 (W-8BEN / W-8BEN-E) tax information.
  if (payload.foreignTin != null) req.setForeignTin(payload.foreignTin);
  if (payload.usTin != null) req.setUsTin(payload.usTin);
  if (payload.countryOfTaxResidence != null) {
    req.setCountryOfTaxResidence(payload.countryOfTaxResidence);
  }
  if (payload.countryOfCitizenship != null) {
    req.setCountryOfCitizenship(payload.countryOfCitizenship);
  }
  if (payload.countryOfIncorporation != null) {
    req.setCountryOfIncorporation(payload.countryOfIncorporation);
  }
  if (payload.entityClassification != null) {
    req.setEntityClassification(payload.entityClassification);
  }

  return request<BillingPublisherAddressType>(client, client.updateBillingAddress, req);
};

export const getInvoicesApi = (
  page: number,
  limit: number,
  searchString?: string,
  dateRange?: DateRangeNumberType
): Promise<BillingPublisherInvoiceType[]> => {
  const client = publisherBillingService.getInstance();
  const invoiceRequest = new PublisherInvoiceRequest();

  invoiceRequest.setPage(page);
  invoiceRequest.setLimit(limit);

  if (searchString) {
    invoiceRequest.setQuery(searchString);
  }

  if (dateRange) {
    const timestampRange = new common_pb.TimestampRange();

    timestampRange.setFrom(dateRange.startDate);
    timestampRange.setTo(dateRange.endDate);

    invoiceRequest.setDateRange(timestampRange);
  }

  return streamRequest<BillingPublisherInvoiceType[]>(client, client.getInvoices, invoiceRequest);
};

export const getWithdrawalsApi = (
  page: number,
  limit: number,
  searchString?: string,
  dateRange?: DateRangeNumberType
): Promise<BillingPublisherWithdrawalType[]> => {
  const client = publisherBillingService.getInstance();
  const invoiceRequest = new PublisherWithdrawalRequest();

  invoiceRequest.setPage(page);
  invoiceRequest.setLimit(limit);

  if (searchString) {
    invoiceRequest.setQuery(searchString);
  }

  if (dateRange) {
    const timestampRange = new common_pb.TimestampRange();

    timestampRange.setFrom(dateRange.startDate);
    timestampRange.setTo(dateRange.endDate);

    invoiceRequest.setDateRange(timestampRange);
  }

  return streamRequest<BillingPublisherWithdrawalType[]>(
    client,
    client.getWithdrawals,
    invoiceRequest
  );
};

export const generateWithdrawalPdfApi = (
  invoiceId: number,
  workspaceId: number
): Promise<FileResponseType> => {
  const client = publisherBillingService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(invoiceId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<FileResponseType>(client, client.generateWithdrawalPdf, idRequest);
};

export const generateInvoicePdfApi = (
  invoiceId: number,
  workspaceId: number
): Promise<FileResponseType> => {
  const client = publisherBillingService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(invoiceId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<FileResponseType>(client, client.generateInvoicePdf, idRequest);
};
